import React from 'react';
import { Settings as SettingsIcon, Globe, Eye, EyeOff, Loader2, Check, X } from 'lucide-react';
import { FormData, Errors } from './SettingList';


interface WebsiteSectionProps {
    formData: FormData;
    errors: Errors;
    handleInputChange: (field: keyof FormData, value: string) => void;
    showEmailPassword: boolean;
    setShowEmailPassword: (value: boolean) => void;
    handleSubmit: () => void; 
    isLoading: boolean;
}

const WebsiteSection: React.FC<WebsiteSectionProps> = ({
    formData,
    errors,
    handleInputChange,
    showEmailPassword,
    setShowEmailPassword,
    handleSubmit,
    isLoading,
}) => {
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-3 sm:p-4 border border-green-100 dark:border-gray-600">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-2 flex items-center">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-md flex items-center justify-center mr-1">
                            <SettingsIcon className="w-3 h-3 text-white" />
                        </div>
                        Mail Configuration
                    </h3>
                    <div className="space-y-2">
                        <div>
                            <label
                                htmlFor="mail-mailer"
                                className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5"
                            >
                                Mail Mailer
                            </label>
                            <div className="relative">
                                <input
                                    id="mail-mailer"
                                    type="text"
                                    value={formData.mailMailer}
                                    readOnly
                                    className={`w-full px-2 py-1.5 bg-gray-100 dark:bg-gray-700 border rounded-md text-gray-500 dark:text-gray-400 text-xs cursor-not-allowed ${errors.mailMailer ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                />
                                {formData.mailMailer && !errors.mailMailer ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.mailMailer ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.mailMailer && (
                                <p className="text-red-500 text-xs mt-0.5">{errors.mailMailer}</p>
                            )}
                        </div>
                        <div>
                            <label
                                htmlFor="mail-host"
                                className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5"
                            >
                                Mail Host
                            </label>
                            <div className="relative">
                                <input
                                    id="mail-host"
                                    type="text"
                                    value={formData.mailHost}
                                    onChange={(e) => handleInputChange('mailHost', e.target.value)}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-1 transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.mailHost ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-green-500'}`}
                                />
                                {formData.mailHost && !errors.mailHost ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.mailHost ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.mailHost && (
                                <p className="text-red-500 text-xs mt-0.5">{errors.mailHost}</p>
                            )}
                        </div>
                        <div>
                            <label
                                htmlFor="mail-port"
                                className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5"
                            >
                                Mail Port
                            </label>
                            <div className="relative">
                                <input
                                    id="mail-port"
                                    type="number"
                                    value={formData.mailPort}
                                    onChange={(e) => handleInputChange('mailPort', e.target.value)}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-1 transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.mailPort ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-green-500'}`}
                                />
                                {formData.mailPort && !errors.mailPort ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.mailPort ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.mailPort && (
                                <p className="text-red-500 text-xs mt-0.5">{errors.mailPort}</p>
                            )}
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-3 sm:p-4 border border-purple-100 dark:border-gray-600">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-2 flex items-center">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-500 rounded-md flex items-center justify-center mr-1">
                            <Globe className="w-3 h-3 text-white" />
                        </div>
                        Email Credentials
                    </h3>
                    <div className="space-y-2">
                        <div>
                            <label
                                htmlFor="email-address"
                                className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5"
                            >
                                Email Address
                            </label>
                            <div className="relative">
                                <input
                                    id="email-address"
                                    type="email"
                                    value={formData.emailAddress}
                                    onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-1 transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.emailAddress ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-purple-500'}`}
                                />
                                {formData.emailAddress && !errors.emailAddress ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.emailAddress ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.emailAddress && (
                                <p className="text-red-500 text-xs mt-0.5">{errors.emailAddress}</p>
                            )}
                        </div>
                        <div>
                            <label
                                htmlFor="name"
                                className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5"
                            >
                                Name
                            </label>
                            <div className="relative">
                                <input
                                    id="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-1 transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-purple-500'}`}
                                />
                                {formData.name && !errors.name ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.name ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.name && (
                                <p className="text-red-500 text-xs mt-0.5">{errors.name}</p>
                            )}
                        </div>
                        <div>
                            <label
                                htmlFor="email-password"
                                className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5"
                            >
                                Email Password
                            </label>
                            <div className="relative">
                                <input
                                    id="email-password"
                                    type={showEmailPassword ? 'text' : 'password'}
                                    value={formData.emailPassword}
                                    onChange={(e) => handleInputChange('emailPassword', e.target.value)}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-1 transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs pr-8 ${errors.emailPassword ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-purple-500'}`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowEmailPassword(!showEmailPassword)}
                                    className="absolute right-1.5 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    {showEmailPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                </button>
                            </div>
                            {errors.emailPassword && (
                                <p className="text-red-500 text-xs mt-0.5">{errors.emailPassword}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-end gap-1.5 pt-2">
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-[#2F6AA7] hover:bg-[#265891] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2F6AA7] disabled:opacity-50"
                >
                    {isLoading ? <Loader2 className="w-3 h-3 mr-0.5 animate-spin" /> : null}
                    Save Email Settings
                </button>
            </div>
        </div>
    );
};

export default WebsiteSection;